import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { DotGridPattern } from "@/ui/components/shared/dot-grid-pattern";

interface BreadcrumbItem {
	label: string;
	href: string;
}

interface CategoryHeroProps {
	title: string;
	description?: string | null;
	backgroundImage?: string | null;
	breadcrumbs: BreadcrumbItem[];
}

export function CategoryHero({ title, description, breadcrumbs }: CategoryHeroProps) {
	return (
		<section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-[var(--surface-primary-soft)] to-transparent">
			<DotGridPattern className="pointer-events-none absolute inset-0 text-primary" id="dot-grid-hero" />
			<div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-20">
				{/* Breadcrumbs */}
				<nav className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground">
					{breadcrumbs.map((crumb, index) => (
						<span key={crumb.href} className="flex items-center gap-1.5">
							{index > 0 && <ChevronRight className="h-3.5 w-3.5" />}
							{index === breadcrumbs.length - 1 ? (
								<span className="font-medium text-foreground">{crumb.label}</span>
							) : (
								<Link href={crumb.href} className="transition-colors hover:text-foreground">
									{crumb.label}
								</Link>
							)}
						</span>
					))}
				</nav>

				<h1
					className="animate-fade-in-up text-3xl font-semibold tracking-tight opacity-0 sm:text-4xl md:text-5xl"
					style={{ animationDelay: "0ms" }}
				>
					{title}
				</h1>
				{description && (
					<p
						className="mt-3 max-w-lg animate-fade-in-up whitespace-pre-line text-sm text-muted-foreground opacity-0 sm:text-base"
						style={{ animationDelay: "100ms" }}
					>
						{description}
					</p>
				)}
			</div>
		</section>
	);
}
